import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { ActiveUserStore } from '../stores/user/active-user.state';
import { BookmarkService } from '../services/bookmark/bookmark.service';
import { BookmarkHelperService } from '../services/bookmark/bookmark-helper.service';
import { ConfirmationDialogService } from '../services/confirmation-dialog.service';
import { GridStore } from '../stores/grid/grid.state';
import { DialogService } from 'primeng/dynamicdialog';
import { PasswordProtectionDialogBookmarkComponent } from '../components/organisms/password-protection-dialog-bookmark/password-protection-dialog-bookmark.component';
import { GuestLinkService } from '../services/bookmark/guest-link.service';
import { Bookmark } from '../models/bookmark';

@Injectable({ providedIn: 'root' })
export class GuestLinkGuard implements CanActivate {
  private bookmarkService = inject(BookmarkService);
  private bookmarkHelper = inject(BookmarkHelperService);
  private guestLinkService = inject(GuestLinkService);
  private activeUserStore = inject(ActiveUserStore);
  private gridStore = inject(GridStore);
  private dialogService = inject(ConfirmationDialogService);
  private dynamicDialogService = inject(DialogService);
  private router = inject(Router);

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    //const token = route.paramMap.get('token');
    const token = route.paramMap.get('id');

    if (!token) {
      this.router.navigate(['/']);
      return false;
    }

    try {
      const bookmark = await this.bookmarkService.getByGuestToken(token);

      if (!bookmark || !bookmark.guestLink?.enabled) {
        this.dialogService.addErrorMessage(
          'Der angeforderte Link ist nicht ungültig.',
          'Nicht gefunden'
        );
        this.router.navigate(['/']);
        return false;
      }

      // Check expiration
      if (bookmark.guestLink.expiresAt) {
        const expiryDate = new Date(bookmark.guestLink.expiresAt);
        if (expiryDate < new Date()) {
          this.dialogService.addErrorMessage(
            'Der angeforderte Link ist nicht mehr gültig.',
            'Abgelaufen'
          );
          this.router.navigate(['/']);
          return false;
        }
      }

      // Check password protection
      if (bookmark.guestLink?.password) {
        const passwordValid = await this.validatePassword(bookmark);
        if (!passwordValid) {
          this.router.navigate(['/']);
          return false;
        }
      }

      // Set guest access configuration in store
      this.activeUserStore.setGuestAccessConfig(
        bookmark,
        this.bookmarkHelper.getBookmarkIndicators(bookmark),
        this.bookmarkHelper.getBookmarkDataLayers(bookmark)
      );

      this.gridStore.setActiveBookmarkById(bookmark._id, bookmark);
      return true;
    } catch (error) {
      console.error('Error validating guest token:', error);
      this.router.navigate(['/']);
      return false;
    }
  }

  private async validatePassword(bookmark: Bookmark): Promise<boolean> {
    return new Promise(resolve => {
      const dialogRef = this.dynamicDialogService.open(
        PasswordProtectionDialogBookmarkComponent,
        {
          header: 'Ansicht geschützt',
          width: '400px',
          modal: true,
          closable: false,
          data: {
            bookmark: bookmark,
          },
        }
      );

      dialogRef.onClose.subscribe(async result => {
        if (result?.action === 'submit') {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }
}
